/* Entra group membership. A sibling of GraphClient, not an extension of it.
 *
 * GraphClient is bound to one SharePoint site and asks for Sites.ReadWrite.All
 * in its constructor. This needs neither: different scope, different endpoints,
 * and nothing about a site. Bolting it on would have meant every list read
 * carrying a directory permission it has no use for.
 *
 * ONE SCOPE, NOT THREE
 *
 * The hub reads membership with POST /users/{id}/getMemberGroups, which asks
 * about a USER and therefore needs User.ReadBasic.All and GroupMember.Read.All
 * on top of the write permission. Blueprint already has everybody's graphId
 * from the nightly sync, so it can ask the question the other way round — who
 * is in these groups — which GroupMember.ReadWrite.All covers on its own.
 *
 * WHAT WILL GO WRONG, AND WHAT IT WILL LOOK LIKE
 *
 * GroupMember.ReadWrite.All is necessary and not sufficient. The signed-in
 * administrator must ALSO be an owner of each group, or hold an active Groups
 * Administrator role — and a role assigned through PIM but not activated fails
 * identically to having no permission at all. explainDirectoryRefusal() in
 * @hird/access exists to say so, because the message Entra sends does not.
 */

import { explainDirectoryRefusal } from '@hird/access';
import { GraphError, type TokenProvider } from '../sharepoint/GraphClient';

const GRAPH = 'https://graph.microsoft.com/v1.0';

/** Graph's JSON batch takes 20 requests. Not a tuning choice — it refuses 21. */
const BATCH_MAX = 20;

interface BatchResponse {
  responses: { id: string; status: number; body?: unknown }[];
}

export class DirectoryClient {
  constructor(
    private readonly getToken: TokenProvider,
    private readonly scopes: string[] = ['GroupMember.ReadWrite.All'],
  ) {}

  private async request<T>(url: string, init: RequestInit = {}): Promise<T> {
    const token = await this.getToken(this.scopes);
    const res = await fetch(url.startsWith('http') ? url : `${GRAPH}${url}`, {
      ...init,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...init.headers,
      },
    });

    if (!res.ok) {
      const body = await res.json().catch(() => null);
      const detail = (body as { error?: { message?: string } } | null)?.error?.message;
      const raw = detail ? `Graph ${res.status}: ${detail}` : `Graph ${res.status} on ${url}`;
      /* The directory's refusals are the one case where the message Entra sends
         is actively misleading — "insufficient privileges" for a caller who has
         the permission but is not an owner of the group reads as a consent
         problem and is not one. */
      throw new GraphError(explainDirectoryRefusal(new Error(raw)) ?? raw, res.status, body);
    }
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  /** Who is in each of these groups, as a map of group id to member object ids.
   *
   *  transitiveMembers rather than members: a level group that has another
   *  group nested inside it would otherwise report the nested group's id and
   *  none of the people in it, and the person would read as having no level at
   *  all. Nothing nests today; this costs nothing and stops it mattering if
   *  somebody ever does it.
   */
  async membersOf(groupIds: readonly string[]): Promise<Map<string, Set<string>>> {
    const out = new Map<string, Set<string>>();
    const ids = [...new Set(groupIds)];

    for (let i = 0; i < ids.length; i += BATCH_MAX) {
      const chunk = ids.slice(i, i + BATCH_MAX);
      const body = {
        requests: chunk.map((id, n) => ({
          id: String(n),
          method: 'GET',
          url: `/groups/${id}/transitiveMembers?$select=id&$top=999`,
        })),
      };
      const res = await this.request<BatchResponse>('/$batch', {
        method: 'POST',
        body: JSON.stringify(body),
      });

      for (const r of res.responses ?? []) {
        const groupId = chunk[Number(r.id)];
        if (!groupId) continue;
        /* A per-request failure costs that group, not the batch. A group that
           has been deleted in Entra but is still in the model would otherwise
           take every other answer down with it. */
        if (r.status >= 400) continue;
        const members = (r.body as { value?: { id?: string }[] } | undefined)?.value ?? [];
        out.set(groupId, new Set(members.map((m) => m.id).filter((x): x is string => !!x)));
      }
    }

    return out;
  }

  /** Idempotent: already a member is not a failure. */
  async addMember(groupId: string, personId: string): Promise<void> {
    try {
      await this.request(`/groups/${groupId}/members/$ref`, {
        method: 'POST',
        body: JSON.stringify({
          '@odata.id': `${GRAPH}/directoryObjects/${personId}`,
        }),
      });
    } catch (err) {
      if (!alreadyMember(err)) throw err;
    }
  }

  /** Idempotent: not a member is not a failure, and neither is a group that has
   *  since been deleted — both mean the membership this was asked to remove
   *  does not exist, which is the state it was asked to produce. */
  async removeMember(groupId: string, personId: string): Promise<void> {
    try {
      await this.request(`/groups/${groupId}/members/${personId}/$ref`, { method: 'DELETE' });
    } catch (err) {
      if (!notAMember(err)) throw err;
    }
  }
}

function alreadyMember(err: unknown): boolean {
  if (!(err instanceof GraphError)) return false;
  /* Graph says 400 with "already exist" rather than a conflict code. Matching
     the status alone would swallow a malformed id, which is a real failure. */
  return err.status === 400 && /already exist|One or more added object references/i.test(err.message);
}

function notAMember(err: unknown): boolean {
  return err instanceof GraphError && (err.status === 404 || /does not exist/i.test(err.message));
}
